// Analytics tracking utility for BeeMerce

class AnalyticsTracker {
  private baseUrl: string;

  constructor() {
    this.baseUrl = window.location.origin;
  }

  // Track page view
  async trackPageView(pagePath: string) {
    try {
      await this.trackEvent('page_view', pagePath);
    } catch (error) {
      console.warn('Failed to track page view:', error);
    }
  }

  // Track custom event
  async trackEvent(event: string, page: string, additionalData?: Record<string, any>) {
    try {
      const data = {
        event,
        page,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...additionalData
      };

      // Send to our analytics endpoint (no auth required for tracking)
      await fetch(`${this.baseUrl}/api/v1/admin/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.warn('Failed to track event:', error);
    }
  }

  // Track product view
  async trackProductView(productId: number, productName: string) {
    await this.trackEvent('product_view', window.location.pathname, {
      product_id: productId,
      product_name: productName
    });
  }

  // Track button click
  async trackButtonClick(buttonName: string, location?: string) {
    await this.trackEvent('button_click', location || window.location.pathname, {
      button_name: buttonName
    });
  }

  // Track purchase intent (add to cart)
  async trackPurchaseIntent(productId: number, productName: string, price: number) {
    await this.trackEvent('purchase_intent', window.location.pathname, {
      product_id: productId,
      product_name: productName,
      price: price
    });
  }
}

// Create singleton instance
const analytics = new AnalyticsTracker();

// Auto-track page views on navigation
let lastPath = '';
let trackingEnabled = false;
let cleanupFn: (() => void) | null = null;

const trackPageViewOnNavigation = () => {
  const currentPath = window.location.pathname;
  if (currentPath !== lastPath) {
    lastPath = currentPath;
    analytics.trackPageView(currentPath);
  }
};

// Initialize tracking (call this from your app's entry point)
export const initializeTracking = () => {
  if (trackingEnabled) return;

  trackingEnabled = true;

  // Track initial page view
  analytics.trackPageView(window.location.pathname);

  // Track navigation changes
  window.addEventListener('popstate', trackPageViewOnNavigation);

  // Track pushState/replaceState (for SPAs)
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function(...args) {
    originalPushState.apply(history, args);
    // Use requestAnimationFrame instead of setTimeout for better performance
    requestAnimationFrame(trackPageViewOnNavigation);
  };

  history.replaceState = function(...args) {
    originalReplaceState.apply(history, args);
    // Use requestAnimationFrame instead of setTimeout for better performance
    requestAnimationFrame(trackPageViewOnNavigation);
  };

  // Cleanup function
  cleanupFn = () => {
    window.removeEventListener('popstate', trackPageViewOnNavigation);
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    trackingEnabled = false;
  };
};

// Cleanup tracking (call this when unmounting the app if needed)
export const cleanupTracking = () => {
  if (cleanupFn) {
    cleanupFn();
    cleanupFn = null;
  }
};

// Auto-initialize for backward compatibility (but should be called explicitly)
if (typeof window !== 'undefined') {
  initializeTracking();
}

export default analytics;